// src/pages/driver-management/DriverScorePeriodicPage.jsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import DataTable from '../../components/DataTable';
import ActionMenu from '../../components/ActionMenu';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import { useNotifier } from '../../components/Notifier';
import { useLanguage } from '../../utils/i18n';
import { useCompanyList } from '../../stores/companyStore';
import { useDriverScorePeriodicList, deleteDriverScorePeriodic } from '../../stores/driverScorePeriodicStore';
import AddDriverScorePeriodicModal from './AddDriverScorePeriodicModal';
import DetailDriverScorePeriodicModal from './DetailDriverScorePeriodicModal';
import dayjs from 'dayjs';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = dayjs(dateStr);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : dateStr;
};

export default function DriverScorePeriodicPage() {
  const { t } = useLanguage();
  const notify = useNotifier();

  // Pagination & Search
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchRaw, setSearchRaw] = useState('');

  // Company Filter
  const { companyOptions } = useCompanyList();
  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored && stored !== 'all' ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const match = companyOptionsNormalized.find((o) => String(o.value) === String(companyId));
      if (match) {
        setSelectedCompanies((prev) => {
          if (prev.length === 1 && String(prev[0].value) === String(match.value)) return prev;
          return [{ value: String(match.value), label: match.label }];
        });
      }
    } catch (e) {
      console.error('auto select company failed', e);
    }
  }, [companyOptionsNormalized]);

  const companyIdsStr = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  // Data fetching hook
  const {
    data: scorePeriodics,
    totalRecord,
    isLoading,
    revalidate
  } = useDriverScorePeriodicList(paginationModel.page, paginationModel.pageSize, 'driver_name', searchRaw, companyIdsStr);

  // Modal (Add/Edit) state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Detail Modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detailRow, setDetailRow] = useState(null);

  // Delete Confirm Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingName, setDeletingName] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Handlers
  const handlePageChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSearch = ({ value }) => {
    setSearchRaw(value ?? '');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleCompanyChange = (newCompanies) => {
    setSelectedCompanies(newCompanies);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleOpenAdd = () => {
    setEditingId(null);
    setModalOpen(true);
  };

  const handleEdit = (id) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const handleView = (id, row) => {
    setDetailId(id);
    setDetailRow(row);
    setDetailModalOpen(true);
  };

  const handleDeletePrompt = (id, row) => {
    setDeletingId(id);
    setDeletingName(`${row?.driver_name || 'Driver'} (${formatDate(row?.start_time)})`);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingId) return;
    setDeleteLoading(true);
    try {
      await deleteDriverScorePeriodic(deletingId);
      notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
      setDeleteDialogOpen(false);
      setDeletingId(null);
      setDeletingName('');
      revalidate();
    } catch (err) {
      console.error('Failed to delete score periodic:', err);
      const errMsg = err?.response?.data?.message || err?.message || t('notify.deleteFailed', 'Failed to delete');
      notify({ message: errMsg, severity: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleModalSuccess = () => {
    revalidate();
  };

  // Table Columns
  const columns = useMemo(
    () => [
      {
        field: 'company_name',
        headerName: t('general.company', 'Company'),
        flex: 1,
        minWidth: 150
      },
      {
        field: 'driver_name',
        headerName: t('masterDriver.driver', 'Driver'),
        flex: 1.2,
        minWidth: 180,

        renderCell: (params) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
              {params.value || '-'}
            </Typography>
          </Box>
        )
      },
      {
        field: 'start_time',
        headerName: t('driverScore.startTime', 'Start Time'),
        flex: 1.1,
        minWidth: 160,
        valueGetter: (val, row) => (row.start_time ? dayjs(row.start_time).format('DD MMM YYYY HH:mm') : '-')
      },
      {
        field: 'end_time',
        headerName: t('driverScore.endTime', 'End Time'),
        flex: 1.1,
        minWidth: 160,
        valueGetter: (val, row) => (row.end_time ? dayjs(row.end_time).format('DD MMM YYYY HH:mm') : '-')
      },
      {
        field: 'score_config_name',
        headerName: t('driverScore.scoreConfig', 'Score Config'),
        flex: 1.2,
        minWidth: 180
      },

      {
        field: 'actions',
        headerName: t('general.action', 'Action'),
        width: 100,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: (params) => (
          <ActionMenu
            row={params.row}
            mode="inline"
            onView={() => handleView(params.row.id, params.row)}
            onEdit={() => handleEdit(params.row.id)}
            onDelete={() => handleDeletePrompt(params.row.id, params.row)}
          />
        )
      }
    ],
    [t]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', gap: 2 }}>
      {/* Top Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">{t('driverScore.driverScorePeriodic', 'Driver Score Periodic')}</Typography>
      </Box>

      {/* Main DataTable */}
      <DataTable
        title={t('driverScore.driverScorePeriodic', 'Driver Score Periodic')}
        rows={scorePeriodics || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecord || 0}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ value }) => handleSearch({ value })}
        onAddClick={handleOpenAdd}
        searchLabel={t('driverScore.searchDriver', 'Search driver name...')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
      />

      {/* Add / Edit Modal */}
      <AddDriverScorePeriodicModal
        open={modalOpen}
        id={editingId}
        onClose={() => {
          setModalOpen(false);
          setEditingId(null);
        }}
        onSuccess={handleModalSuccess}
      />

      {/* Detail Modal */}
      <DetailDriverScorePeriodicModal
        open={detailModalOpen}
        periodicId={detailId}
        initialRow={detailRow}
        onClose={() => {
          setDetailModalOpen(false);
          setDetailId(null);
          setDetailRow(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        loading={deleteLoading}
        title={t('general.confirmDelete', 'Confirm Delete')}
        message={`${t('general.deleteConfirmation', 'Are you sure you want to delete')} "${deletingName}"?`}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeletingId(null);
          setDeletingName('');
        }}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
}
